import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Camera, Check, X } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '../../context/AuthContext';
import { documentsAPI } from '../../lib/api';
import { nouvelId, wetstockApi } from '../../lib/wetstockApi';
import { messageErreur } from '../../lib/erreurs';
import { libelle, ton } from '../../lib/libelles';
import { formatDateHeure, formatNombre, formatPourcent, formatPrixUnitaire } from '../../lib/formats';
import { chartVolume, receptionVariances, to15c } from '../../lib/stationCalc';
import { PageHeader } from '../../components/ui/page-header';
import { Aide } from '../../components/ui/aide';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { LoadingState } from '../../components/ui/loading-state';
import { EmptyState } from '../../components/ui/empty-state';
import { useDroitsStation } from '../../components/stations/StationPicker';

/**
 * Réception d'un camion (lot 031, maquette « Cuves et réception ») : jaugeage
 * de la cuve avant puis après le dépotage, pige du camion à l'arrivée,
 * scellés, photos ; le reçu en cuve à 15 °C et l'écart par maillon (manquant de
 * transport, écart de réception) s'affichent avant la validation. Le serveur
 * refait le calcul et écrit une seule ligne de stock.
 */

const CELLULES =
  'w-full border-collapse text-sm [&_td]:border-b [&_td]:border-border/60 [&_td]:px-3 [&_td]:py-2 [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-border [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground [&_tr:last-child_td]:border-0 [&_tfoot_td]:border-t [&_tfoot_td]:border-border [&_tfoot_td]:font-semibold';

const PHOTOS = [
  ['pige', 'Pige'],
  ['scelles', 'Scellés'],
  ['bon', 'Bon de livraison'],
];

const vide = (valeur) => valeur === null || valeur === undefined || valeur === '';
const nombre = (valeur) => {
  if (vide(valeur)) return null;
  const n = Number(String(valeur).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};
const litres = (valeur) => (vide(valeur) ? '—' : formatNombre(valeur, 0));
const signe = (valeur) => {
  if (vide(valeur)) return '—';
  const n = Number(valeur);
  const texte = formatNombre(Math.abs(n), 0);
  if (Math.round(n) > 0) return `+${texte}`;
  if (Math.round(n) < 0) return `−${texte}`;
  return texte;
};

const Panneau = ({ titre, aide, droite, children }) => (
  <section className="rounded-xl border border-border bg-card shadow-sm">
    <header className="flex flex-wrap items-baseline justify-between gap-2 px-4 pb-2 pt-3">
      <h2 className="flex items-center gap-1 text-base font-semibold">
        {titre}
        {aide}
      </h2>
      {droite}
    </header>
    <div className="overflow-x-auto px-1 pb-3">{children}</div>
  </section>
);

const Saisie = ({ id, valeur, onChange, pas = '0.1', min = '0', max, label, largeur = 'w-24' }) => (
  <Input
    id={id}
    type="number"
    inputMode="decimal"
    min={min}
    max={max}
    step={pas}
    value={valeur}
    onChange={(event) => onChange(event.target.value)}
    aria-label={label}
    className={`ml-auto h-8 ${largeur} text-right tabular-nums outline outline-2 outline-offset-1 outline-flame/60`}
  />
);

/** Litres lus et à 15 °C d'une pige saisie, comme le serveur les calculera. */
const mesurer = (pige, reception) => {
  if (!pige) return { lu: null, corrige: null, probleme: '' };
  try {
    let lu = null;
    if (reception.has_chart && (reception.chart_points || []).length) {
      if (nombre(pige.height_cm) === null) return { lu: null, corrige: null, probleme: '' };
      lu = chartVolume(reception.chart_points, nombre(pige.height_cm));
    } else {
      lu = nombre(pige.volume_liters);
    }
    if (lu === null) return { lu: null, corrige: null, probleme: '' };
    return { lu, corrige: to15c(lu, nombre(pige.temperature_c), reception.expansion_coeff), probleme: '' };
  } catch (probleme) {
    return { lu: null, corrige: null, probleme: probleme.message };
  }
};

const depuisServeur = (dip) => (dip ? { lu: dip.volume_liters, corrige: dip.volume_15c, probleme: '' } : { lu: null, corrige: null, probleme: '' });

const LigneJaugeage = ({ titre, dip, saisie, setSaisie, editable, avecBareme, mesure, prefixe }) => (
  <tr>
    <td className="whitespace-nowrap font-medium">{titre}</td>
    <td className="text-right tabular-nums">
      {editable && avecBareme ? (
        <Saisie id={`${prefixe}-hauteur`} valeur={saisie.height_cm} onChange={(v) => setSaisie((a) => ({ ...a, height_cm: v }))} label={`${titre} : hauteur en cm`} />
      ) : dip && !vide(dip.height_cm) ? (
        `${formatNombre(dip.height_cm, 1)} cm`
      ) : (
        '—'
      )}
    </td>
    <td className="text-right tabular-nums">
      {editable ? (
        <Saisie id={`${prefixe}-temperature`} valeur={saisie.temperature_c} onChange={(v) => setSaisie((a) => ({ ...a, temperature_c: v }))} min="-10" max="70" label={`${titre} : température en °C`} largeur="w-20" />
      ) : dip && !vide(dip.temperature_c) ? (
        `${formatNombre(dip.temperature_c, 1)} °C`
      ) : (
        '—'
      )}
    </td>
    <td className="text-right tabular-nums">
      {editable && !avecBareme ? (
        <Saisie id={`${prefixe}-volume`} valeur={saisie.volume_liters} onChange={(v) => setSaisie((a) => ({ ...a, volume_liters: v }))} pas="1" label={`${titre} : volume lu en litres`} largeur="w-28" />
      ) : (
        `${litres(mesure.lu)}${vide(mesure.lu) ? '' : ' L'}`
      )}
    </td>
    <td className="text-right font-medium tabular-nums">{vide(mesure.corrige) ? '—' : `${litres(mesure.corrige)} L`}</td>
  </tr>
);

const AnnulerDialog = ({ open, onOpenChange, onConfirm }) => {
  const [motif, setMotif] = useState('');
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (open) setMotif('');
  }, [open]);
  const confirmer = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      await onConfirm(motif.trim());
      onOpenChange(false);
    } finally {
      setEnCours(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Annuler la réception</DialogTitle>
        </DialogHeader>
        <form onSubmit={confirmer} className="grid gap-4">
          <div className="grid gap-1.5">
            <label htmlFor="annuler-motif" className="text-sm font-medium">
              Motif
            </label>
            <Input id="annuler-motif" value={motif} onChange={(e) => setMotif(e.target.value)} minLength={3} maxLength={500} required />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Retour
            </Button>
            <Button type="submit" variant="destructive" disabled={enCours || motif.trim().length < 3}>
              Annuler la réception
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ReceptionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canSeeMoney } = useAuth();
  const droits = useDroitsStation();
  const [reception, setReception] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [introuvable, setIntrouvable] = useState(false);
  const [avant, setAvant] = useState({ height_cm: '', temperature_c: '', volume_liters: '' });
  const [apres, setApres] = useState({ height_cm: '', temperature_c: '', volume_liters: '' });
  const [arrivee, setArrivee] = useState('');
  const [scelles, setScelles] = useState(null);
  const [photos, setPhotos] = useState({});
  const [envoi, setEnvoi] = useState(false);
  const [annuler, setAnnuler] = useState(false);
  // Une clé par étape : un renvoi après coupure réseau rend le même résultat.
  const cleEtape = useRef(nouvelId());

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const lue = await wetstockApi.receptions.get(id);
      setReception(lue);
      setArrivee(vide(lue?.truck_arrival_liters_15c) ? '' : String(lue.truck_arrival_liters_15c));
      setScelles(lue?.seals_ok ?? null);
      setIntrouvable(false);
    } catch (probleme) {
      setIntrouvable(true);
      toast.error(messageErreur(probleme, 'Impossible de charger la réception'));
    } finally {
      setChargement(false);
    }
  }, [id]);

  useEffect(() => {
    charger();
  }, [charger]);

  const statut = reception?.status;
  const ouverte = statut === 'EXPECTED' || statut === 'IN_PROGRESS';
  const peutEcrire = droits.peutEcrire('purchases') || droits.peutEcrire('tanks');
  const avecBareme = Boolean(reception?.has_chart && (reception?.chart_points || []).length);
  const saisirAvant = peutEcrire && statut === 'EXPECTED';
  const saisirApres = peutEcrire && statut === 'IN_PROGRESS';

  const mesureAvant = useMemo(
    () => (saisirAvant ? mesurer(avant, reception || {}) : depuisServeur(reception?.dip_before)),
    [saisirAvant, avant, reception],
  );
  const mesureApres = useMemo(
    () => (saisirApres ? mesurer(apres, reception || {}) : depuisServeur(reception?.dip_after)),
    [saisirApres, apres, reception],
  );

  // Reçu en cuve et écarts : ceux du serveur une fois validée, l'aperçu avant.
  const calcul = useMemo(() => {
    if (!reception) return null;
    if (statut === 'VALIDATED') {
      return {
        recuLu: reception.received_liters,
        recu15: reception.received_liters_15c,
        transport: reception.transport_variance_liters,
        reception: reception.reception_variance_liters,
        total: reception.total_variance_liters,
        pourcent: reception.total_variance_percent,
        sansCorrection: reception.uncorrected_variance_liters,
        tolerance: reception.tolerance_liters,
        depasse: reception.exceeds,
      };
    }
    if (vide(mesureAvant.corrige) || vide(mesureApres.corrige)) return null;
    const recu15 = Math.round((Number(mesureApres.corrige) - Number(mesureAvant.corrige)) * 100) / 100;
    const recuLu = Math.round((Number(mesureApres.lu) - Number(mesureAvant.lu)) * 100) / 100;
    try {
      const ecarts = receptionVariances({
        delivered15c: reception.delivered_liters_15c,
        received15c: recu15,
        loaded15c: reception.loaded_liters_15c,
        truckArrival15c: nombre(arrivee),
        receivedLiters: recuLu,
        settings: { variance_percent: reception.variance_percent_setting, variance_liters: reception.variance_liters_setting },
      });
      return {
        recuLu,
        recu15,
        transport: ecarts.transport_variance_liters,
        reception: ecarts.reception_variance_liters,
        total: ecarts.total_variance_liters,
        pourcent: ecarts.total_variance_percent,
        sansCorrection: ecarts.uncorrected_variance_liters,
        tolerance: ecarts.tolerance_liters,
        depasse: ecarts.exceeds,
      };
    } catch {
      return null;
    }
  }, [reception, statut, mesureAvant, mesureApres, arrivee]);

  const piges = (saisie) =>
    avecBareme
      ? { height_cm: nombre(saisie.height_cm) ?? undefined, temperature_c: nombre(saisie.temperature_c) ?? undefined }
      : {
          volume_liters: nombre(saisie.volume_liters) ?? undefined,
          temperature_c: nombre(saisie.temperature_c) ?? undefined,
        };

  const etapeFaite = (suivante, message) => {
    cleEtape.current = nouvelId();
    setReception(suivante);
    toast.success(message);
  };

  const commencer = async () => {
    setEnvoi(true);
    try {
      const suivante = await wetstockApi.receptions.start(
        id,
        {
          dip_before: { ...piges(avant), photo_document_id: photos.pige?.id },
          truck_arrival_liters_15c: nombre(arrivee) ?? undefined,
          seals_ok: scelles ?? undefined,
        },
        cleEtape.current,
      );
      etapeFaite(suivante, 'Dépotage commencé : jaugez la cuve à la fin');
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible de commencer le dépotage'));
    } finally {
      setEnvoi(false);
    }
  };

  const valider = async () => {
    setEnvoi(true);
    try {
      const suivante = await wetstockApi.receptions.validate(
        id,
        {
          dip_after: piges(apres),
          truck_arrival_liters_15c: nombre(arrivee) ?? undefined,
          seals_ok: scelles ?? undefined,
          photos: Object.values(photos)
            .map((photo) => photo?.id)
            .filter(Boolean),
        },
        cleEtape.current,
      );
      etapeFaite(suivante, `Réception validée : ${litres(suivante?.received_liters_15c)} L à 15 °C en cuve`);
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible de valider la réception'));
    } finally {
      setEnvoi(false);
    }
  };

  const confirmerAnnulation = async (motif) => {
    try {
      setReception(await wetstockApi.receptions.cancel(id, motif));
      toast.success('Réception annulée');
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible d’annuler la réception'));
      throw probleme;
    }
  };

  const joindre = (cle) => async (event) => {
    const fichier = event.target.files?.[0];
    if (!fichier) return;
    try {
      const document = await documentsAPI.upload(fichier, 'OTHER');
      setPhotos((avant) => ({ ...avant, [cle]: document }));
      toast.success('Photo jointe');
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Photo non envoyée'));
    }
  };

  if (chargement && !reception) {
    return (
      <div className="mx-auto w-full max-w-page px-4 py-5 md:px-6 lg:px-8">
        <LoadingState shape="table" />
      </div>
    );
  }
  if (introuvable || !reception) {
    return (
      <div className="mx-auto w-full max-w-page px-4 py-5 md:px-6 lg:px-8">
        <EmptyState
          variant="error"
          title="Réception introuvable"
          description="Revenez à l’écran des cuves pour retrouver les réceptions de la station."
          action={
            <Button type="button" onClick={() => navigate('/stations/cuves')}>
              Cuves et réceptions
            </Button>
          }
        />
      </div>
    );
  }

  const pretAvant = saisirAvant && !mesureAvant.probleme && !vide(mesureAvant.corrige);
  const pretApres = saisirApres && !mesureApres.probleme && !vide(mesureApres.corrige) && calcul && Number(calcul.recu15) > 0;
  const probleme = mesureAvant.probleme || mesureApres.probleme;
  const camion = reception.truck_label || 'Camion';
  const reference = reception.loaded_liters_15c ?? reception.delivered_liters_15c;

  return (
    <div className="mx-auto w-full max-w-page space-y-4 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Stations · Cuves et réceptions"
        title={`Réception du camion ${camion}`}
        description="Jaugez la cuve avant et après le dépotage ; l’écran calcule le reçu à 15 °C et l’écart de chaque maillon."
        meta={[
          [reception.product_name, reception.tank_name].filter(Boolean).join(' vers '),
          reception.delivery_note_number ? `bon de livraison ${reception.delivery_note_number}` : null,
        ]
          .filter(Boolean)
          .join(', ')}
        alertes={[
          scelles === false && { niveau: 'critique', texte: 'Scellés non intacts', court: 'Scellés' },
          calcul?.depasse && { niveau: 'critique', texte: 'Écart au-dessus du seuil', court: 'Écart' },
          reception.exceeds_ullage && { niveau: 'attention', texte: 'Le bon dépasse la place libre de la cuve', court: 'Place en cuve' },
        ].filter(Boolean)}
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => navigate('/stations/cuves')}>
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Cuves
            </Button>
            {peutEcrire && ouverte && (
              <Button type="button" variant="outline" onClick={() => setAnnuler(true)}>
                <X className="h-4 w-4" aria-hidden="true" />
                Annuler
              </Button>
            )}
            {saisirAvant && (
              <Button type="button" onClick={commencer} disabled={envoi || !pretAvant}>
                Commencer le dépotage
              </Button>
            )}
            {saisirApres && (
              <Button type="button" onClick={valider} disabled={envoi || !pretApres}>
                <Check className="h-4 w-4" aria-hidden="true" />
                Valider la réception
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={ton('station_reception', statut)}>{libelle('station_reception', statut)}</Badge>
        {scelles === true && <Badge variant="success">Scellés intacts</Badge>}
        {scelles === false && <Badge variant="destructive">Scellés non intacts</Badge>}
        {statut === 'VALIDATED' && reception.measured === false && <Badge variant="warning">Sans jaugeage</Badge>}
        {calcul && !calcul.depasse && !vide(calcul.total) && <Badge variant="success">Écart sous le seuil</Badge>}
        {calcul?.depasse && <Badge variant="destructive">Écart au-dessus du seuil</Badge>}
        {reception.validated_at && <span className="text-xs text-muted-foreground">Validée le {formatDateHeure(reception.validated_at)}</span>}
        {statut === 'CANCELLED' && reception.cancel_reason && (
          <span className="text-xs text-muted-foreground">Annulée : {reception.cancel_reason}</span>
        )}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Panneau
          titre={`Jaugeage de ${reception.tank_name || 'la cuve'}`}
          droite={<span className="text-xs text-muted-foreground">{reception.chart_label || (avecBareme ? 'Barème de la cuve' : 'Cuve sans barème')}</span>}
        >
          <table className={CELLULES}>
            <thead>
              <tr>
                <th aria-label="Moment" />
                <th className="text-right">Hauteur</th>
                <th className="text-right">Température</th>
                <th className="text-right">Volume lu</th>
                <th className="text-right">Volume à 15 °C</th>
              </tr>
            </thead>
            <tbody>
              <LigneJaugeage
                titre="Avant le dépotage"
                prefixe="avant"
                dip={reception.dip_before}
                saisie={avant}
                setSaisie={setAvant}
                editable={saisirAvant}
                avecBareme={avecBareme}
                mesure={mesureAvant}
              />
              <LigneJaugeage
                titre="Après le dépotage"
                prefixe="apres"
                dip={reception.dip_after}
                saisie={apres}
                setSaisie={setApres}
                editable={saisirApres}
                avecBareme={avecBareme}
                mesure={mesureApres}
              />
            </tbody>
            <tfoot>
              <tr>
                <td>Reçu en cuve</td>
                <td />
                <td />
                <td className="text-right tabular-nums">{calcul ? `${litres(calcul.recuLu)} L` : '—'}</td>
                <td className="text-right tabular-nums">{calcul ? `${litres(calcul.recu15)} L` : '—'}</td>
              </tr>
            </tfoot>
          </table>
          {probleme && (
            <p className="px-3 pt-2 text-sm font-medium text-destructive" role="alert">
              {probleme}
            </p>
          )}
          {ouverte && peutEcrire && (
            <div className="grid grid-cols-3 gap-2 px-3 pt-3">
              {PHOTOS.map(([cle, texte]) => (
                <label
                  key={cle}
                  className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed p-3 text-xs ${
                    photos[cle] ? 'border-success/50 bg-success/10 text-success' : 'border-border text-muted-foreground hover:bg-muted/40'
                  }`}
                >
                  {photos[cle] ? <Check className="h-4 w-4" aria-hidden="true" /> : <Camera className="h-4 w-4" aria-hidden="true" />}
                  {texte}
                  <input type="file" accept="image/*" capture="environment" className="sr-only" onChange={joindre(cle)} />
                </label>
              ))}
            </div>
          )}
          {!ouverte && (reception.photos || []).length > 0 && (
            <p className="px-3 pt-2 text-xs text-muted-foreground">{reception.photos.length} photo{reception.photos.length > 1 ? 's' : ''} jointe{reception.photos.length > 1 ? 's' : ''}</p>
          )}
        </Panneau>

        <div className="grid content-start gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <div className="text-xs text-muted-foreground">Sans correction</div>
              <div className="font-display text-2xl font-bold tabular-nums">{calcul ? `${signe(calcul.sansCorrection)} L` : '—'}</div>
            </div>
            <div className={`rounded-xl border p-4 shadow-sm ${calcul?.depasse ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-card'}`}>
              <div className="text-xs text-muted-foreground">À 15 °C</div>
              <div className={`font-display text-2xl font-bold tabular-nums ${calcul?.depasse ? 'text-destructive' : ''}`}>{calcul ? `${signe(calcul.total)} L` : '—'}</div>
            </div>
          </div>

          <Panneau
            titre="D’où vient l’écart"
            aide={
              <Aide titre="Écart par maillon">
                Manquant de transport = litres pigés dans le camion à l’arrivée − litres chargés au dépôt. Écart de
                réception = reçu en cuve − litres pigés dans le camion. Les deux sont à 15 °C et ne s’absorbent pas
                l’un l’autre. Sans correction, la dilatation due à la chaleur fausse la comparaison.
              </Aide>
            }
            droite={<span className="text-xs text-muted-foreground">Litres à 15 °C</span>}
          >
            <table className={CELLULES}>
              <tbody>
                <tr>
                  <td>{reception.loaded_liters_15c ? `Chargé${reception.supplier_name ? ` à ${reception.supplier_name}` : ' au dépôt'}` : 'Bon de livraison'}</td>
                  <td className="text-right tabular-nums">{litres(reference)}</td>
                </tr>
                <tr>
                  <td>Pigé dans le camion à l’arrivée</td>
                  <td className="text-right tabular-nums">
                    {ouverte && peutEcrire ? (
                      <Saisie id="arrivee-camion" valeur={arrivee} onChange={setArrivee} pas="1" min="1" label="Litres pigés dans le camion à l’arrivée" largeur="w-28" />
                    ) : (
                      litres(reception.truck_arrival_liters_15c)
                    )}
                  </td>
                </tr>
                <tr>
                  <td>Manquant de transport</td>
                  <td className="text-right tabular-nums">{calcul ? signe(calcul.transport) : '—'}</td>
                </tr>
                <tr>
                  <td>Reçu en cuve</td>
                  <td className="text-right tabular-nums">{calcul ? litres(calcul.recu15) : '—'}</td>
                </tr>
                <tr>
                  <td>Écart de réception</td>
                  <td className="text-right tabular-nums">{calcul ? signe(calcul.reception) : '—'}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    Écart total
                    {calcul && !vide(calcul.pourcent)
                      ? `, ${formatPourcent(Math.abs(calcul.pourcent), 2)} (tolérance ${litres(calcul.tolerance)} L)`
                      : ''}
                  </td>
                  <td className={`text-right tabular-nums ${calcul?.depasse ? 'text-destructive' : ''}`}>{calcul ? signe(calcul.total) : '—'}</td>
                </tr>
              </tfoot>
            </table>
          </Panneau>

          <Panneau titre="Camion et bon">
            <table className={CELLULES}>
              <tbody>
                <tr>
                  <td>Camion</td>
                  <td className="text-right">{camion}</td>
                </tr>
                {reception.driver_name && (
                  <tr>
                    <td>Chauffeur</td>
                    <td className="text-right">{reception.driver_name}</td>
                  </tr>
                )}
                {reception.supplier_name && (
                  <tr>
                    <td>Fournisseur</td>
                    <td className="text-right">{reception.supplier_name}</td>
                  </tr>
                )}
                <tr>
                  <td>Litres du bon (15 °C)</td>
                  <td className="text-right tabular-nums">{litres(reception.delivered_liters_15c)}</td>
                </tr>
                <tr>
                  <td>Scellés</td>
                  <td className="text-right">
                    {ouverte && peutEcrire ? (
                      <span className="inline-flex gap-1">
                        <Button type="button" size="sm" variant={scelles === true ? 'default' : 'outline'} aria-pressed={scelles === true} onClick={() => setScelles(true)}>
                          Intacts
                        </Button>
                        <Button type="button" size="sm" variant={scelles === false ? 'destructive' : 'outline'} aria-pressed={scelles === false} onClick={() => setScelles(false)}>
                          Non intacts
                        </Button>
                      </span>
                    ) : scelles === null ? (
                      'Non vérifiés'
                    ) : scelles ? (
                      'Intacts'
                    ) : (
                      'Non intacts'
                    )}
                  </td>
                </tr>
                {(reception.seal_numbers || []).length > 0 && (
                  <tr>
                    <td>Numéros des scellés</td>
                    <td className="text-right tabular-nums">{reception.seal_numbers.join(', ')}</td>
                  </tr>
                )}
                {canSeeMoney && !vide(reception.landed_cost_per_liter) && (
                  <>
                    <tr>
                      <td>Coût du litre</td>
                      <td className="text-right tabular-nums">{formatPrixUnitaire(reception.unit_cost)}</td>
                    </tr>
                    <tr>
                      <td>Fret au litre</td>
                      <td className="text-right tabular-nums">{formatPrixUnitaire(reception.freight_cost_per_liter || 0)}</td>
                    </tr>
                    <tr>
                      <td>Coût de revient en cuve</td>
                      <td className="text-right font-semibold tabular-nums">{formatPrixUnitaire(reception.landed_cost_per_liter)}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </Panneau>
        </div>
      </div>

      <AnnulerDialog open={annuler} onOpenChange={setAnnuler} onConfirm={confirmerAnnulation} />
    </div>
  );
};

export default ReceptionPage;
